import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaPowerOfAttorneyVehiclePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Power of Attorney for Vehicles</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Vehicle Power of Attorney (VP 136)</h1>
        <p className="text-obsidian-300">How to authorize someone else to handle your vehicle title and registration transactions in Nevada.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">What Is a Vehicle Power of Attorney?</h2>
          <p>A Vehicle Power of Attorney (Form VP 136) allows you to authorize another person — called an agent — to sign title or registration documents on your behalf. This is commonly used when the vehicle owner cannot be present for DMV transactions, such as when using a third-party service provider like Speedy DMV Services.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">When Is It Required?</h2>
          <ul className="space-y-3">
            {[
              'When a third party (like Speedy DMV Services) is handling your registration or title transfer',
              'When the vehicle owner is out of state or unable to appear in person',
              'When a dealer is processing paperwork on behalf of a buyer or seller',
              'When a family member is handling DMV transactions for another family member',
            ].map(item => (
              <li key={item} className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-arrow-right-s-line text-gold-500"></i></div>
                <span className="text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Requirements</h2>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <ul className="space-y-2 text-sm text-amber-800">
              {[
                'Must be completed on Form VP 136 (available at dmv.nv.gov)',
                'Must be notarized — a notary public must witness and stamp the document',
                'Must specify the exact transactions the agent is authorized to perform',
                'Must include the vehicle information (VIN, year, make, model)',
                'The agent must present the original POA (not a copy) when conducting transactions',
              ].map(item => (
                <li key={item} className="flex items-start space-x-2"><i className="ri-information-line flex-shrink-0 mt-0.5"></i><span>{item}</span></li>
              ))}
            </ul>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Notary Services</h2>
          <p className="text-sm">Speedy DMV Services offers notary services to help you properly execute a Vehicle Power of Attorney. Our notary can witness your signature and provide the required notarization for your POA document.</p>
          <Link to="/notary-services" className="inline-flex items-center space-x-2 mt-3 text-gold-600 hover:text-gold-700 text-sm font-semibold">
            <span>Learn About Our Notary Services</span><i className="ri-arrow-right-line"></i>
          </Link>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need a Vehicle POA?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services provides notary services and can help you complete your Vehicle Power of Attorney correctly.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaPowerOfAttorneyVehiclePage;
